import { useState, useEffect } from 'react'
import { ChevronDown, ChevronUp, Eye, Shield } from 'lucide-react'
import { Permission } from '../../types/auth.types'
import type { User } from '../../types/auth.types'
import { getAllUsers, updateUserPermissions } from '../../services/auth.service'
import { useAuth } from '../../context/AuthContext'
import { logAudit } from '../../services/auth.service'

// Group permissions into readable categories
const PERMISSION_GROUPS: { label: string; perms: Permission[] }[] = [
  { label: 'Admin & Access', perms: [Permission.MANAGE_USERS, Permission.EDIT_ACCESS_CONTROL, Permission.VIEW_AUDIT_LOG, Permission.VIEW_ALL_DASHBOARDS, Permission.IMPERSONATE_USER] },
  { label: 'Clients', perms: [Permission.VIEW_CLIENTS, Permission.EDIT_CLIENTS] },
  { label: 'Pipeline / CRM', perms: [Permission.VIEW_PIPELINE, Permission.EDIT_PIPELINE] },
  { label: 'Invoices', perms: [Permission.VIEW_INVOICES, Permission.EDIT_INVOICES] },
  { label: 'Expenses', perms: [Permission.VIEW_EXPENSES, Permission.EDIT_EXPENSES] },
  { label: 'Campaigns & Ads', perms: [Permission.VIEW_CAMPAIGNS, Permission.EDIT_CAMPAIGNS, Permission.VIEW_AD_PERFORMANCE, Permission.EDIT_AD_PERFORMANCE, Permission.VIEW_AD_DASHBOARD, Permission.VIEW_BUDGET_TRACKER] },
  { label: 'Content & Scheduling', perms: [Permission.VIEW_CONTENT_CALENDAR, Permission.EDIT_CONTENT_CALENDAR, Permission.VIEW_SCHEDULING, Permission.EDIT_SCHEDULING] },
  { label: 'Team & Tasks', perms: [Permission.ASSIGN_TASKS, Permission.VIEW_EMPLOYEES, Permission.MANAGE_EMPLOYEES] },
  { label: 'Reports', perms: [Permission.VIEW_REPORTS, Permission.EXPORT_REPORTS] },
  { label: 'Projects', perms: [Permission.VIEW_PROJECTS, Permission.EDIT_PROJECTS] },
  { label: 'Operations', perms: [Permission.VIEW_OPERATIONS_DASHBOARD] },
]

function PermLabel(p: Permission) {
  return p.replace(/_/g, ' ').toLowerCase().replace(/^\w/, c => c.toUpperCase())
}

function Toggle({ on, onChange }: { on: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      onClick={() => onChange(!on)}
      className="relative flex-shrink-0 w-9 h-5 rounded-full transition-all"
      style={{ background: on ? '#ffc600' : '#333' }}
    >
      <div className="absolute top-0.5 w-4 h-4 rounded-full transition-all"
        style={{ background: on ? '#000' : '#555', left: on ? '19px' : '2px' }} />
    </button>
  )
}

function UserPermPanel({ user }: { user: User }) {
  const { currentUser, startImpersonation } = useAuth()
  const [expanded, setExpanded] = useState(false)
  const [perms, setPerms] = useState<Permission[]>(user.permissions)
  const [saved, setSaved] = useState(false)

  if (user.role === 'superadmin') return null // can't edit superadmin perms

  const togglePerm = (p: Permission, on: boolean) => {
    const updated = on ? [...perms, p] : perms.filter(x => x !== p)
    setPerms(updated)
    updateUserPermissions(user.id, updated)
    setSaved(true)
    setTimeout(() => setSaved(false), 2000)

    if (currentUser) {
      logAudit({
        userId: currentUser.id,
        userName: currentUser.name,
        userRole: currentUser.role,
        action: on ? 'GRANT_PERMISSION' : 'REVOKE_PERMISSION',
        module: 'Access Control',
        detail: `${on ? 'Granted' : 'Revoked'} ${p} for ${user.name}`,
        oldValue: on ? 'false' : 'true',
        newValue: on ? 'true' : 'false',
      })
    }
  }

  const roleColor = user.role === 'founder' ? '#ffc600' : '#a0a0a0'

  return (
    <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(255,198,0,0.12)' }}>
      {/* Header row */}
      <div
        className="flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors"
        style={{ background: expanded ? '#1a1a1a' : '#0f0f0f' }}
        onClick={() => setExpanded(v => !v)}
      >
        <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
          style={{ background: roleColor, color: '#000' }}>
          {user.avatarInitials}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-white">{user.name}</p>
          <p className="text-xs" style={{ color: '#555' }}>{user.email}</p>
        </div>
        <span className="text-xs px-2 py-0.5 rounded capitalize font-medium flex-shrink-0"
          style={{ background: 'rgba(255,198,0,0.1)', color: roleColor }}>
          {user.role}
        </span>
        <span className="text-xs flex-shrink-0" style={{ color: '#555' }}>{perms.length} permissions</span>
        {saved && <span className="text-xs flex-shrink-0" style={{ color: '#e8a000' }}>Saved ✓</span>}
        <button
          onClick={e => { e.stopPropagation(); startImpersonation(user) }}
          className="flex items-center gap-1 text-xs px-2 py-1 rounded flex-shrink-0 transition-all"
          style={{ color: '#ffc600', border: '1px solid rgba(255,198,0,0.25)' }}
          title="View as this user"
        >
          <Eye size={11} /> View as
        </button>
        {expanded ? <ChevronUp size={16} style={{ color: '#555' }} /> : <ChevronDown size={16} style={{ color: '#555' }} />}
      </div>

      {/* Permissions panel */}
      {expanded && (
        <div className="px-4 py-4 space-y-5" style={{ background: '#0f0f0f', borderTop: '1px solid rgba(255,198,0,0.08)' }}>
          {PERMISSION_GROUPS.map(group => (
            <div key={group.label}>
              <p className="text-xs font-bold mb-2 uppercase tracking-wider" style={{ color: '#555' }}>{group.label}</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {group.perms.map(p => (
                  <div key={p} className="flex items-center justify-between px-3 py-2 rounded-lg" style={{ background: '#1a1a1a' }}>
                    <span className="text-xs" style={{ color: perms.includes(p) ? '#fff' : '#555' }}>
                      {PermLabel(p)}
                    </span>
                    <Toggle on={perms.includes(p)} onChange={on => togglePerm(p, on)} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default function AccessControl() {
  const [allUsers, setAllUsers] = useState<User[]>([])
  useEffect(() => { getAllUsers().then(setAllUsers) }, [])
  const users     = allUsers.filter(u => u.role !== 'superadmin')
  const founders  = users.filter(u => u.role === 'founder')
  const employees = users.filter(u => u.role === 'employee')

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-3">
        <Shield size={20} style={{ color: '#ffc600' }} />
        <div>
          <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>Access Control</h1>
          <p className="text-sm" style={{ color: '#a0a0a0' }}>Toggle permissions per user — changes apply on their next page load</p>
        </div>
      </div>

      <div className="space-y-6">
        <div>
          <h2 className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: '#ffc600' }}>Founders</h2>
          <div className="space-y-2">
            {founders.map(u => <UserPermPanel key={u.id} user={u} />)}
          </div>
        </div>

        <div>
          <h2 className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: '#a0a0a0' }}>Employees</h2>
          <div className="space-y-2">
            {employees.map(u => <UserPermPanel key={u.id} user={u} />)}
          </div>
        </div>
      </div>
    </div>
  )
}
